import LogRocket from 'logrocket';

import { Auth0IndexedDBCache } from './auth0Cache';

/**
 * Migrates Auth0 tokens from localStorage to IndexedDB cache.
 * This should be called once on app startup to preserve existing sessions
 * when switching from localStorage to IndexedDB cache.
 */
export async function migrateAuth0CacheToIndexedDB(): Promise<void> {
  try {
    const cache = new Auth0IndexedDBCache();
    let migratedCount = 0;

    // Find all Auth0-related keys in localStorage
    const auth0Keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.includes('auth0')) {
        auth0Keys.push(key);
      }
    }

    if (auth0Keys.length === 0) {
      LogRocket.info('No Auth0 tokens found in localStorage to migrate');
      return;
    }

    // Migrate each key to IndexedDB
    await Promise.all(
      auth0Keys.map(async (fullKey: string) => {
        try {
          const value = localStorage.getItem(fullKey);
          if (value) {
            const parsedValue = JSON.parse(value);
            await cache.set(fullKey, parsedValue);
            migratedCount += 1;

            // Remove from localStorage after successful migration
            localStorage.removeItem(fullKey);
          }
        } catch (error) {
          LogRocket.error('Error migrating individual Auth0 key', { key: fullKey, error });
          // Continue with other keys even if one fails
        }
      }),
    );

    if (migratedCount > 0) {
      LogRocket.info('Successfully migrated Auth0 tokens to IndexedDB', {
        count: migratedCount,
        message: 'Tokens will no longer be truncated due to localStorage size limits',
      });
    }
  } catch (error) {
    LogRocket.error('Error during Auth0 cache migration', error);
    // Don't throw - let the app continue even if migration fails
  }
}
